import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["messages", "form", "panel", "notification", "input", "sendButton"]
  static values = { bookingId: Number, currentUserId: Number }

  connect() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create({
      channel: "BookingChatChannel",
      booking_id: this.bookingIdValue
    }, {
      connected: () => {},
      disconnected: () => {},
      received: (data) => {
        this.handleMessageReceived(data)
      }
    })

    this.scrollToBottom()

    this.element.addEventListener('turbo:submit-end', () => {
      setTimeout(() => {
        this.scrollToBottom()
        if (this.hasInputTarget) this.inputTarget.value = ""
        this.updateSendButton()
      }, 100)
    })
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.consumer) this.consumer.disconnect()
  }

  togglePanel() {
    this.panelTarget.classList.toggle("hidden")
    if (!this.panelTarget.classList.contains("hidden")) {
      this.notificationTarget.classList.add("hidden")
      this.scrollToBottom()
      if (this.hasInputTarget) {
        setTimeout(() => this.inputTarget.focus(), 100)
      }
    }
  }

  updateSendButton() {
    if (this.hasSendButtonTarget && this.hasInputTarget) {
      const hasContent = this.inputTarget.value.trim().length > 0
      this.sendButtonTarget.classList.toggle("opacity-30", !hasContent)
      this.sendButtonTarget.classList.toggle("opacity-100", hasContent)
    }
  }

  handleMessageReceived(data) {
    if (data.type === 'message_added') {
      if (data.message.user.id !== this.currentUserIdValue) {
        this.addMessageToChat(data.message)
        if (this.hasPanelTarget && this.panelTarget.classList.contains("hidden")) {
          this.notificationTarget.classList.remove("hidden")
        }
      }
      this.scrollToBottom()
    }
  }

  addMessageToChat(messageData) {
    const isCurrentUser = messageData.user.id === this.currentUserIdValue
    const messageHtml = this.createMessageHTML(messageData, isCurrentUser)
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)
  }

  createMessageHTML(messageData, isCurrentUser) {
    const content = this.escapeHtml(messageData.content)

    if (isCurrentUser) {
      return `
        <div class="flex justify-end">
          <div class="bg-gradient-to-br from-[#1f4d64] to-[#163748] rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-[10px] px-4 pt-2.5 pb-3 max-w-[254px]">
            <div class="text-sm text-white leading-relaxed tracking-[-0.15px]"><p>${content}</p></div>
          </div>
        </div>
      `
    } else {
      const bubble = `
        <div class="flex gap-2 items-end">
          <div class="w-7 h-7 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#1f4d64] to-[#163748]">
            <span class="text-[10px] text-white tracking-wide">${messageData.user.initials}</span>
          </div>
          <div class="bg-gray-100 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-[10px] px-4 pt-2.5 pb-3 max-w-[254px]">
            <div class="text-sm text-[#1e2939] leading-relaxed tracking-[-0.15px]"><p>${content}</p></div>
          </div>
        </div>
      `
      const label = this.senderLabelFor(messageData)
      if (!label) return bubble

      return `
        <div>
          <p class="ml-9 mb-1 text-[11px] text-gray-500 font-medium">${this.escapeHtml(label)}</p>
          ${bubble}
        </div>
      `
    }
  }

  // A broadcast is one payload for every reader, so the label comes with the
  // ids of those who read the message without it.
  senderLabelFor(messageData) {
    const hiddenFrom = messageData.sender_label_hidden_from || []
    if (hiddenFrom.includes(this.currentUserIdValue)) return null

    return messageData.sender_label
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }
}
